import type { AssetId, ChainId } from '@shapeshiftoss/caip'
import type {
  BTCSignTxInput,
  BTCSignTxOutput,
  BTCWallet,
  HDWallet,
  PublicKey,
} from '@shapeshiftoss/hdwallet-core'
import {
  bip32ToAddressNList,
  BTCOutputAddressType,
  supportsBTC,
} from '@shapeshiftoss/hdwallet-core'
import type { Bip44Params, RootBip44Params, UtxoChainId } from '@shapeshiftoss/types'
import { KnownChainIds, UtxoAccountType } from '@shapeshiftoss/types'
import type * as unchained from '@shapeshiftoss/unchained-client'
import WAValidator from 'multicoin-address-validator'
import PQueue from 'p-queue'

import type { ChainAdapter as IChainAdapter } from '../api'
import { ChainAdapterError, ErrorHandler } from '../error/ErrorHandler'
import type {
  Account,
  BroadcastTransactionInput,
  BuildSendTxInput,
  FeeDataEstimate,
  GetBip44ParamsInput,
  GetFeeDataInput,
  SignAndBroadcastTransactionInput,
  SignTx,
  SignTxInput,
  SubscribeError,
  SubscribeTxsInput,
  Transaction,
  TransferType,
  TxHistoryInput,
  TxHistoryResponse,
  TxTransfer,
  UtxoBuildSendApiTxInput,
  ValidAddressResult,
} from '../types'
import { CONTRACT_INTERACTION, ValidAddressResultType } from '../types'
import {
  accountTypeToOutputScriptType,
  accountTypeToScriptType,
  chainIdToChainLabel,
  convertXpubVersion,
  toAddressNList,
  toRootDerivationPath,
  verifyLedgerAppOpen,
} from '../utils'
import { bn, bnOrZero } from '../utils/bignumber'
import { assertAddressNotSanctioned } from '../utils/validateAddress'
import type { GetAddressInput, GetUtxosInput } from './types'
import { getAddresses } from './utils'
import { utxoSelect } from './utxoSelect'

export const utxoChainIds = [
  KnownChainIds.BitcoinMainnet,
  KnownChainIds.BitcoinCashMainnet,
  KnownChainIds.DogecoinMainnet,
  KnownChainIds.LitecoinMainnet,
  KnownChainIds.ZcashMainnet,
] as const

export type UtxoChainAdapter = UtxoBaseAdapter<UtxoChainId>

export interface ChainAdapterArgs {
  chainId?: UtxoChainId
  coinName: string
  providers: {
    http:
      | unchained.bitcoin.V1Api
      | unchained.bitcoincash.V1Api
      | unchained.dogecoin.V1Api
      | unchained.litecoin.V1Api
      | unchained.zcash.V1Api
    ws: unchained.ws.Client<unchained.utxo.types.Tx>
  }
  thorMidgardUrl: string
}

export interface UtxoBaseAdapterArgs extends ChainAdapterArgs {
  assetId: AssetId
  chainId: UtxoChainId
  rootBip44Params: RootBip44Params
  defaultUtxoAccountType: UtxoAccountType
  parser: unchained.utxo.BaseTransactionParser<unchained.utxo.types.Tx>
  supportedAccountTypes: UtxoAccountType[]
  supportedChainIds: ChainId[]
}

export abstract class UtxoBaseAdapter<T extends UtxoChainId> implements IChainAdapter<T> {
  protected readonly chainId: UtxoChainId
  protected readonly coinName: string
  protected readonly rootBip44Params: RootBip44Params
  protected readonly defaultUtxoAccountType: UtxoAccountType
  protected readonly supportedChainIds: ChainId[]
  protected readonly supportedAccountTypes: UtxoAccountType[]
  protected readonly providers: {
    http:
      | unchained.bitcoin.V1Api
      | unchained.bitcoincash.V1Api
      | unchained.dogecoin.V1Api
      | unchained.litecoin.V1Api
      | unchained.zcash.V1Api
    ws: unchained.ws.Client<unchained.utxo.types.Tx>
  }

  protected assetId: AssetId
  protected accountAddresses: Record<string, string[]> = {}
  protected parser: unchained.utxo.BaseTransactionParser<unchained.utxo.types.Tx>

  protected constructor(args: UtxoBaseAdapterArgs) {
    this.assetId = args.assetId
    this.chainId = args.chainId
    this.coinName = args.coinName
    this.rootBip44Params = args.rootBip44Params
    this.defaultUtxoAccountType = args.defaultUtxoAccountType
    this.parser = args.parser
    this.providers = args.providers
    this.supportedAccountTypes = args.supportedAccountTypes
    this.supportedChainIds = args.supportedChainIds

    if (!this.supportedChainIds.includes(this.chainId)) {
      throw new Error(`${this.chainId} not supported. (supported: ${this.supportedChainIds})`)
    }
  }

  abstract getType(): T
  abstract getFeeAssetId(): AssetId
  abstract getName(): string
  abstract getDisplayName(): string

  protected assertIsAccountTypeSupported(accountType: UtxoAccountType) {
    if (!this.supportedAccountTypes.includes(accountType)) {
      throw new Error(`${accountType} not supported. (supported: ${this.supportedAccountTypes})`)
    }
  }

  protected assertSupportsChain(wallet: HDWallet): asserts wallet is BTCWallet {
    if (!supportsBTC(wallet)) {
      throw new ChainAdapterError(`wallet does not support: ${this.getDisplayName()}`, {
        translation: 'chainAdapters.errors.unsupportedChain',
        options: { chain: this.getDisplayName() },
      })
    }
  }

  getChainId(): ChainId {
    return this.chainId
  }

  getSupportedAccountTypes(): UtxoAccountType[] {
    return this.supportedAccountTypes
  }

  getCoinName(): string {
    return this.coinName
  }

  getBip44Params({
    accountNumber,
    accountType,
    addressIndex,
    isChange = false,
  }: GetBip44ParamsInput): Bip44Params {
    if (accountNumber < 0) throw new Error('accountNumber must be >= 0')
    if (addressIndex !== undefined && addressIndex < 0) throw new Error('addressIndex must be >= 0')

    const purpose = (() => {
      switch (accountType) {
        case UtxoAccountType.SegwitNative:
          return 84
        case UtxoAccountType.SegwitP2sh:
          return 49
        case UtxoAccountType.P2pkh:
          return 44
        default:
          throw new Error(`unsupported account type: ${accountType}`)
      }
    })()

    return { ...this.rootBip44Params, accountNumber, purpose, isChange, addressIndex }
  }

  async getAccount(pubkey: string): Promise<Account<T>> {
    try {
      const data = await this.providers.http.getAccount({ pubkey })

      const balance = bnOrZero(data.balance).plus(bnOrZero(data.unconfirmedBalance))

      // cache addresses for getTxHistory to use without needing to make extra requests
      this.accountAddresses[data.pubkey] = data.addresses?.map(address => address.pubkey) ?? [
        data.pubkey,
      ]

      return {
        balance: balance.toString(),
        chain: this.getType(),
        chainId: this.chainId,
        assetId: this.assetId,
        chainSpecific: {
          addresses: data.addresses,
          nextChangeAddressIndex: data.nextChangeAddressIndex,
          nextReceiveAddressIndex: data.nextReceiveAddressIndex,
        },
        pubkey: data.pubkey,
      } as Account<T>
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.getAccount',
        options: { pubkey },
      })
    }
  }

  async getAddress({
    wallet,
    accountNumber,
    accountType = this.defaultUtxoAccountType,
    addressIndex,
    isChange = false,
    showOnDevice = false,
    pubKey,
  }: GetAddressInput): Promise<string> {
    try {
      this.assertIsAccountTypeSupported(accountType)
      if (!wallet) throw new Error('wallet is required')
      this.assertSupportsChain(wallet)

      const bip44Params = this.getBip44Params({
        accountNumber,
        accountType,
        isChange,
        addressIndex,
      })

      const account = await (async () => {
        if (pubKey || bip44Params.addressIndex === undefined) {
          return this.getAccount(
            pubKey ?? (await this.getPublicKey(wallet, accountNumber, accountType)).xpub,
          )
        }
      })()

      const nextAddressIndex = bip44Params.isChange
        ? account?.chainSpecific.nextChangeAddressIndex
        : account?.chainSpecific.nextReceiveAddressIndex

      const targetAddressIndex = bip44Params.addressIndex ?? nextAddressIndex ?? 0

      const address = await (async () => {
        if (pubKey) return account?.chainSpecific.addresses?.[targetAddressIndex]?.pubkey

        await verifyLedgerAppOpen(this.chainId, wallet)

        return wallet.btcGetAddress({
          addressNList: toAddressNList({ ...bip44Params, addressIndex: targetAddressIndex }),
          coin: this.coinName,
          scriptType: accountTypeToScriptType[accountType],
          showDisplay: showOnDevice,
        })
      })()

      if (!address) throw new Error('error getting address from wallet')

      return address
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.getAddress',
      })
    }
  }

  async buildSendApiTransaction(input: UtxoBuildSendApiTxInput<T>): Promise<SignTx<T>> {
    try {
      const {
        value,
        to,
        xpub,
        accountNumber,
        sendMax = false,
        chainSpecific,
        skipToAddressValidation,
      } = input
      const { from, satoshiPerByte, accountType, opReturnData } = chainSpecific

      if (!value) throw new Error('value is required')
      if (!to) throw new Error('to is required')
      if (!satoshiPerByte) throw new Error('satoshiPerByte is required')

      this.assertIsAccountTypeSupported(accountType)

      const utxos = await this.providers.http.getUtxos({ pubkey: xpub })

      const coinSelectResult = utxoSelect({
        utxos,
        from,
        to,
        satoshiPerByte,
        sendMax,
        value,
        opReturnData,
        assetId: this.assetId,
      })

      if (!coinSelectResult?.inputs || !coinSelectResult?.outputs) {
        throw new Error('error selecting utxos')
      }

      const { inputs, outputs } = coinSelectResult

      const account = await this.getAccount(xpub)

      // Always validate next change and receive addresses
      const nextChangeAddressIndex = account.chainSpecific.nextChangeAddressIndex ?? 0
      const nextReceiveAddressIndex = account.chainSpecific.nextReceiveAddressIndex ?? 0
      const nextChangeAddressInput = {
        address: account.chainSpecific.addresses?.[nextChangeAddressIndex]?.pubkey,
      }
      const nextReceiveAddressInput = {
        address: account.chainSpecific.addresses?.[nextReceiveAddressIndex]?.pubkey,
      }

      const addresses = [...inputs, ...outputs, nextChangeAddressInput, nextReceiveAddressInput]
        .map(({ address }) => (skipToAddressValidation && address === to ? null : address))
        .filter(Boolean) as string[]

      const uniqueAddresses = [...new Set(addresses)]

      await Promise.all(uniqueAddresses.map(assertAddressNotSanctioned))

      const bip44Params = this.getBip44Params({
        accountNumber,
        accountType,
        isChange: true,
        addressIndex: account.chainSpecific.nextChangeAddressIndex,
      })

      const signTxInputs: BTCSignTxInput[] = []
      for (const input of inputs) {
        const data = await (async () => {
          try {
            return await this.providers.http.getTransaction({ txid: input.txid })
          } catch (error) {
            if (this.chainId === KnownChainIds.ZcashMainnet) {
              const response = await fetch(
                `https://api.blockchair.com/zcash/raw/transaction/${input.txid}`,
              )
              const data = await response.json()
              if (!response.ok || data.error) {
                throw new Error(`Blockchair API error: ${data.error || response.statusText}`)
              }
              return {
                hex: data.data[input.txid].raw_transaction,
              }
            }
            throw error
          }
        })()

        signTxInputs.push({
          // UTXO inputs are not guaranteed to have paths.
          // They will in the case of an /api/v1/account/<xpub>/utxos account
          // However, if we got utxos from an /api/v1/account/<address>/utxos account (i.e UTXOs for a single address), these will *not* contain a path, which is fine,
          // as the path will be the same for *all* UTXOs i.e can be derived from BIP44Params (we're dealing with a single address)
          addressNList: input.path
            ? bip32ToAddressNList(input.path)
            : toAddressNList({ ...bip44Params }),
          scriptType: accountTypeToScriptType[accountType],
          amount: String(input.value),
          vout: input.vout,
          txid: input.txid,
          hex: data.hex,
          // For Zcash, we need to pass the blockHeight and txid of each input transaction
          // so Ledger can add them to the PSBT and determine the correct consensus branch ID.
          // Only pass blockHeight if it's a valid positive number (mempool txs have blockHeight: -1)
          ...(this.coinName === 'Zcash' &&
            'blockHeight' in data &&
            typeof data.blockHeight === 'number' &&
            data.blockHeight > 0 && { blockHeight: data.blockHeight }),
        })
      }

      const addressNList = toAddressNList(bip44Params)

      const signTxOutputs = outputs.map<BTCSignTxOutput>(output => {
        if (output.address) {
          return {
            addressType: BTCOutputAddressType.Spend,
            amount: String(output.value),
            address: output.address,
          }
        }

        return {
          addressType: BTCOutputAddressType.Change,
          amount: String(output.value),
          addressNList,
          scriptType: accountTypeToOutputScriptType[accountType],
          isChange: true,
        }
      })

      const txToSign = {
        coin: this.coinName,
        inputs: signTxInputs,
        outputs: signTxOutputs,
        opReturnData,
      } as SignTx<T>

      return txToSign
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.buildTransaction',
      })
    }
  }

  async buildSendTransaction(input: BuildSendTxInput<T>): Promise<{ txToSign: SignTx<T> }> {
    try {
      const { wallet, accountNumber, chainSpecific, pubKey } = input

      this.assertSupportsChain(wallet)

      const xpub = await (async () => {
        if (pubKey) return pubKey
        return (await this.getPublicKey(wallet, accountNumber, chainSpecific.accountType)).xpub
      })()
      const txToSign = await this.buildSendApiTransaction({ ...input, xpub })

      return { txToSign }
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.buildTransaction',
      })
    }
  }

  async getFeeData({
    to,
    value,
    chainSpecific: { from, pubkey, opReturnData },
    sendMax = false,
  }: GetFeeDataInput<T>): Promise<FeeDataEstimate<T>> {
    try {
      if (!to) throw new Error('to is required')
      if (!value) throw new Error('value is required')
      if (!pubkey) throw new Error('pubkey is required')

      const data = await this.providers.http.getNetworkFees()

      if (
        !(data.fast?.satsPerKiloByte && data.average?.satsPerKiloByte && data.slow?.satsPerKiloByte)
      ) {
        throw new Error('error to get network fees')
      }

      // ensure higher confirmation speeds never have lower fees than lower confirmation speeds
      if (data.slow.satsPerKiloByte > data.average.satsPerKiloByte)
        data.average.satsPerKiloByte = data.slow.satsPerKiloByte
      if (data.average.satsPerKiloByte > data.fast.satsPerKiloByte)
        data.fast.satsPerKiloByte = data.average.satsPerKiloByte

      const utxos = await this.providers.http.getUtxos({ pubkey })

      const utxoSelectInput = {
        from,
        to,
        value,
        opReturnData,
        utxos,
        sendMax,
        assetId: this.assetId,
      }

      // We have to round because coinselect library uses sats per byte which cant be decimals
      const fastPerByte = String(Math.round(data.fast.satsPerKiloByte / 1000))
      const averagePerByte = String(Math.round(data.average.satsPerKiloByte / 1000))
      const slowPerByte = String(Math.round(data.slow.satsPerKiloByte / 1000))

      const { fee: fastFee } = utxoSelect({ ...utxoSelectInput, satoshiPerByte: fastPerByte })
      const { fee: averageFee } = utxoSelect({ ...utxoSelectInput, satoshiPerByte: averagePerByte })
      const { fee: slowFee } = utxoSelect({ ...utxoSelectInput, satoshiPerByte: slowPerByte })

      return {
        fast: { txFee: String(fastFee), chainSpecific: { satoshiPerByte: fastPerByte } },
        average: { txFee: String(averageFee), chainSpecific: { satoshiPerByte: averagePerByte } },
        slow: { txFee: String(slowFee), chainSpecific: { satoshiPerByte: slowPerByte } },
      } as FeeDataEstimate<T>
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.getFeeData',
      })
    }
  }

  async signTransaction({ txToSign, wallet }: SignTxInput<SignTx<T>>): Promise<string> {
    try {
      this.assertSupportsChain(wallet)
      await verifyLedgerAppOpen(this.chainId, wallet)

      const signedTx = await wallet.btcSignTx(txToSign)

      if (!signedTx?.serializedTx) throw new Error('error signing tx')

      return signedTx.serializedTx
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.signTransaction',
      })
    }
  }

  async signAndBroadcastTransaction({
    receiverAddress,
    signTxInput,
  }: SignAndBroadcastTransactionInput<T>): Promise<string> {
    try {
      const { wallet } = signTxInput

      if (receiverAddress !== CONTRACT_INTERACTION) {
        await assertAddressNotSanctioned(receiverAddress)
      }

      this.assertSupportsChain(wallet)

      const hex = await this.signTransaction(signTxInput)

      return this.broadcastTransaction({ hex })
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.signAndBroadcastTransaction',
      })
    }
  }

  async getTxHistory(input: TxHistoryInput): Promise<TxHistoryResponse> {
    const requestQueue = input.requestQueue ?? new PQueue()

    if (!this.accountAddresses[input.pubkey]) {
      await requestQueue.add(() => this.getAccount(input.pubkey))
    }

    const data = await requestQueue.add(() =>
      this.providers.http.getTxHistory({
        pubkey: input.pubkey,
        pageSize: input.pageSize,
        cursor: input.cursor,
      }),
    )

    const transactions = await Promise.all(
      data.txs.reduce<Promise<Transaction>[]>((prev, tx) => {
        if (input.knownTxIds?.has(tx.txid)) return prev
        prev.push(requestQueue.add(() => this.parseTx(tx, input.pubkey)))
        return prev
      }, []),
    )

    return {
      cursor: data.cursor ?? '',
      pubkey: input.pubkey,
      transactions,
      txIds: data.txs.map(tx => tx.txid),
    }
  }

  async broadcastTransaction({ hex }: Pick<BroadcastTransactionInput, 'hex'>): Promise<string> {
    try {
      const txHash = await (async () => {
        try {
          return await this.providers.http.sendTx({ sendTxBody: { hex } })
        } catch (error) {
          if (this.chainId === KnownChainIds.ZcashMainnet) {
            const response = await fetch('https://api.blockchair.com/zcash/push/transaction', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ data: hex }),
            })
            const data = await response.json()
            if (!response.ok || data.error) {
              throw new Error(`Blockchair API error: ${data.error || response.statusText}`)
            }
            return data.data.transaction_hash
          }
          throw error
        }
      })()

      return txHash
    } catch (err) {
      if ((err as Error).name === 'ResponseError') {
        const response = await ((err as any).response as Response).json()
        const error = JSON.parse(response.message)

        return ErrorHandler(JSON.stringify(response), {
          translation: 'chainAdapters.errors.broadcastTransactionWithMessage',
          options: { message: error.message },
        })
      }

      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.broadcastTransaction',
      })
    }
  }

  async subscribeTxs(
    input: SubscribeTxsInput,
    onMessage: (msg: Transaction) => void,
    onError: (err: SubscribeError) => void,
  ): Promise<void> {
    const { wallet, accountNumber, accountType = this.defaultUtxoAccountType } = input

    const bip44Params = this.getBip44Params({ accountNumber, accountType })
    const subscriptionId = `${toRootDerivationPath(bip44Params)}/${accountType}`

    const account = await this.getAccount(
      input.pubKey ?? (await this.getPublicKey(wallet, accountNumber, accountType)).xpub,
    )

    const addresses = (account.chainSpecific.addresses ?? []).map(address => address.pubkey)

    await this.providers.ws.subscribeTxs(
      subscriptionId,
      { topic: 'txs', addresses },
      async msg => onMessage(await this.parseTx(msg.data, account.pubkey)),
      err => onError({ message: err.message }),
    )
  }

  unsubscribeTxs(input?: SubscribeTxsInput): void {
    if (!input) return this.providers.ws.unsubscribeTxs()

    const { accountNumber, accountType = this.defaultUtxoAccountType } = input

    const bip44Params = this.getBip44Params({ accountNumber, accountType })
    const subscriptionId = `${toRootDerivationPath(bip44Params)}/${accountType}`

    this.providers.ws.unsubscribeTxs(subscriptionId, { topic: 'txs', addresses: [] })
  }

  closeTxs(): void {
    this.providers.ws.close('txs')
  }

  // eslint-disable-next-line require-await
  async validateAddress(address: string): Promise<ValidAddressResult> {
    const chainLabel = chainIdToChainLabel(this.chainId)
    const isValidAddress = WAValidator.validate(address, chainLabel)
    if (isValidAddress) return { valid: true, result: ValidAddressResultType.Valid }
    return { valid: false, result: ValidAddressResultType.Invalid }
  }

  async getPublicKey(
    wallet: HDWallet,
    accountNumber: number,
    accountType: UtxoAccountType,
  ): Promise<PublicKey> {
    try {
      this.assertIsAccountTypeSupported(accountType)
      await verifyLedgerAppOpen(this.chainId, wallet)

      const bip44Params = this.getBip44Params({ accountNumber, accountType })
      const path = toRootDerivationPath(bip44Params)

      const publicKeys = await wallet.getPublicKeys([
        {
          coin: this.coinName,
          addressNList: bip32ToAddressNList(path),
          curve: 'secp256k1',
          scriptType: accountTypeToScriptType[accountType],
        },
      ])

      if (!publicKeys?.[0]) throw new Error('error getting public key from wallet')

      if (accountType) {
        return { xpub: convertXpubVersion(publicKeys[0].xpub, accountType) }
      }

      return publicKeys[0]
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.getPublicKey',
      })
    }
  }

  async getPublicKeys(
    wallet: HDWallet,
    accountNumbers: number[],
    accountTypes: UtxoAccountType[],
  ): Promise<Record<number, Record<UtxoAccountType, PublicKey>>> {
    try {
      await verifyLedgerAppOpen(this.chainId, wallet)

      accountTypes.forEach(accountType => this.assertIsAccountTypeSupported(accountType))

      const requests = accountNumbers.flatMap(accountNumber =>
        accountTypes.map(accountType => {
          const bip44Params = this.getBip44Params({ accountNumber, accountType })
          const path = toRootDerivationPath(bip44Params)

          return {
            coin: this.coinName,
            addressNList: bip32ToAddressNList(path),
            curve: 'secp256k1' as const,
            scriptType: accountTypeToScriptType[accountType],
            _accountNumber: accountNumber,
            _accountType: accountType,
          }
        }),
      )

      const publicKeys = await wallet.getPublicKeys(requests)
      if (!publicKeys) return {}

      const result: Record<number, Record<UtxoAccountType, PublicKey>> = {}

      requests.forEach((request, i) => {
        const pubKey = publicKeys[i]
        if (!pubKey) return

        const accountNumber = request._accountNumber
        const accountType = request._accountType

        if (!result[accountNumber]) result[accountNumber] = {} as Record<UtxoAccountType, PublicKey>

        result[accountNumber][accountType] = {
          xpub: convertXpubVersion(pubKey.xpub, accountType),
        }
      })

      return result
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.getPublicKey',
      })
    }
  }

  async getUtxos(input: GetUtxosInput): Promise<unchained.utxo.types.Utxo[]> {
    try {
      const utxos = await this.providers.http.getUtxos(input)
      return utxos
    } catch (err) {
      return ErrorHandler(err, {
        translation: 'chainAdapters.errors.getUtxos',
      })
    }
  }

  async parseTx(tx: unchained.utxo.types.Tx, pubkey: string): Promise<Transaction> {
    if (!this.accountAddresses[pubkey]) {
      const data = await this.providers.http.getAccount({ pubkey })

      // cache addresses for getTxHistory to use without needing to make extra requests
      this.accountAddresses[data.pubkey] = data.addresses?.map(address => address.pubkey) ?? [
        data.pubkey,
      ]
    }

    const {
      ownedAddresses,
      ownedSendAddresses,
      unownedReceiveAddresses,
      receiveAddresses,
      sendAddresses,
      ownedReceiveAddresses,
    } = getAddresses(tx, this.accountAddresses[pubkey])

    // a send transaction where all outputs are sent to owned addresses
    const isSelfSend =
      ownedSendAddresses.length &&
      receiveAddresses.every(address => ownedAddresses.includes(address))

    let transaction = {} as Omit<Transaction, 'transfers'>
    const transfers: Record<TransferType, TxTransfer[]> = { Contract: [], Send: [], Receive: [] }
    for (const address of ownedAddresses) {
      const { address: _, ...parsedTx } = await this.parser.parse(tx, address)

      // create transaction object with all shared properties
      if (!Object.keys(transaction).length) {
        transaction = { ...parsedTx, pubkey }
      }

      // add fee if it exists on any of the parsed transactions
      if (parsedTx.fee) transaction.fee = parsedTx.fee

      parsedTx.transfers.forEach(transfer => {
        if (!transfers['Send'][0] && transfer.type === 'Send') {
          // calculate total output amount excluding change (unless self send)
          const totalOutput = tx.vout.reduce((prev, vout) => {
            const isOwnedOutput = vout.addresses?.some(address => ownedAddresses.includes(address))

            if (isOwnedOutput && !isSelfSend) return prev

            return prev.plus(vout.value)
          }, bn(0))

          transfers[transfer.type][0] = {
            assetId: transfer.assetId,
            from: sendAddresses,
            to: isSelfSend ? ownedReceiveAddresses : unownedReceiveAddresses,
            type: transfer.type,
            value: totalOutput.toString(),
          }
        }

        if (transfer.type === 'Receive') {
          const isChange =
            ownedSendAddresses.length &&
            receiveAddresses.filter(address => ownedAddresses.includes(address)).length === 1

          // exclude change outputs (unless self send)
          if (isChange && !isSelfSend) return

          transfers[transfer.type].push({
            assetId: transfer.assetId,
            from: sendAddresses,
            to: [transfer.to],
            type: transfer.type,
            value: transfer.totalValue,
          })
        }
      })
    }

    return Object.assign(transaction, { transfers: Object.values(transfers).flat() })
  }

  get httpProvider():
    | unchained.utxo.bitcoin.V1Api
    | unchained.utxo.bitcoincash.V1Api
    | unchained.utxo.dogecoin.V1Api
    | unchained.utxo.litecoin.V1Api
    | unchained.utxo.zcash.V1Api {
    return this.providers.http
  }

  get wsProvider(): unchained.ws.Client<unchained.utxo.types.Tx> {
    return this.providers.ws
  }
}
